import { PlaneEntryProps } from '@ixicare/resources';
import { Alert, Button, FileDrop, IconButton, Modal, Tooltip } from '@ixicare/ui';
import React from 'react';
import { Spacer } from '~components/Utils/Spacer';
import { t } from '~i18n';
import { MAX_FILE_SIZE, useMapFile } from '~modules/sites/hooks/useMapFile';
import classnames from 'classnames';
import styles from '../../containers/SitePlaneMap.module.css';
import SiteStore from '~modules/sites/store/Site';
import { UserAction } from '~config/notification';
import { useMutation } from '@tanstack/react-query';
import { humaniseErrors } from '~utils/error';

interface UploadFloorMapModalProps {
  onClose: () => void;
  onRefresh: () => any;
  floor: PlaneEntryProps;
  site: SiteStore;
}

export const UploadFloorMapModal = ({ onClose, onRefresh, floor, site }: UploadFloorMapModalProps) => {
  const iconRef = React.useRef<HTMLElement>(null!);
  const [mapFile, setMapFile] = React.useState<File>();

  const { onInvalid, isFileValid, setIsFileValid, handleFloorPlanChange } = useMapFile({
    setMapFile,
    notificationsStore: site.rootStore.notifications,
  });

  const uploadMutation = useMutation({
    mutationFn: () => site.updatePlane(floor.id, { mapFile }),
    onSuccess: () => {
      onClose();
      onRefresh();
    },
    onError: () => {
      site.rootStore.notifications.addUserActionNotification({
        type: UserAction.SiteFloorUpdate,
        notificationType: 'error',
        message: t('alerts.site.floor.upload.failure'),
      });
    },
  });

  const onChangeCallback = React.useCallback(
    (file?: File) => {
      handleFloorPlanChange(file);
      uploadMutation.reset();
    },
    [handleFloorPlanChange, uploadMutation]
  );

  return (
    <Modal>
      <h3 className="text-center mb-10">{t('site.floors.uploadFloorPlan')}</h3>
      {uploadMutation.error && <Alert type="error">{humaniseErrors(uploadMutation.error as Error).title}</Alert>}
      <Tooltip title="File requirements" content={t('site.form.image.tooltip')} triggerRef={iconRef}>
        <>
          <label className={classnames(styles.FileDropLabel)}>
            {t('site.floors.file')} <IconButton ref={iconRef} icon="question" />
          </label>
          <FileDrop
            onChange={onChangeCallback}
            onInvalid={onInvalid}
            isFileValid={isFileValid}
            setIsFileValid={setIsFileValid}
            maxSize={MAX_FILE_SIZE}
            value={mapFile}
            label={t('site.form.image.label.inner')}
            clearText={t('site.form.image.clearSelection')}
          />
        </>
      </Tooltip>
      <Spacer size={32} />
      <Button
        className="mb-15 error"
        disabled={!mapFile || !isFileValid || uploadMutation.isPending}
        loading={uploadMutation.isPending}
        isBlock
        buttonType="success"
        type="button"
        onClick={() => uploadMutation.mutate()}
      >
        {t('site.floors.upload')}
      </Button>
      <Button onClick={() => onClose()} disabled={uploadMutation.isPending} isBlock buttonType="link">
        {t('global.cancel')}
      </Button>
    </Modal>
  );
};
